import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';   // NAV-1
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { Pill, AlertTriangle, Check, X } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { toast } from 'sonner';

export default function MedicationDashboardPage() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchParams] = useSearchParams();   // NAV-1: dashboard "Medication misses" deep link
  const [statusFilter, setStatusFilter] = useState(() => (['MISSED', 'ADMINISTERED', 'REFUSED', 'WITHHELD'].includes(searchParams.get('status')) ? searchParams.get('status') : 'all'));
  const [stats, setStats] = useState({ administered: 0, missed: 0, refused: 0, withheld: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Get recent eMAR records via export
        const r = await api.get('/emar/export-csv');
        const lines = r.data.split('\n').filter(l => l.trim());
        const header = lines[0]?.split(',') || [];
        const statusIdx = header.indexOf('Status');
        let administered = 0, missed = 0, refused = 0, withheld = 0;
        const recs = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',');
          const status = cols[statusIdx]?.trim();
          if (status === 'ADMINISTERED' || status === 'PRN_GIVEN') administered++;   // BS3-013: a PRN dose given is given
          else if (status === 'MISSED') missed++;
          else if (status === 'REFUSED') refused++;
          else if (status === 'WITHHELD') withheld++;
          recs.push({
            id: i,
            participant: cols[0]?.trim(),
            medication: cols[1]?.trim(),
            dosage: cols[2]?.trim(),
            scheduled_time: cols[3]?.trim(),
            status: status,
            administered_at: cols[5]?.trim(),
            staff: cols[6]?.trim(),
          });
        }
        setStats({ administered, missed, refused, withheld });
        const filtered = statusFilter === 'all' ? recs
          : recs.filter(r => r.status === statusFilter || (statusFilter === 'ADMINISTERED' && r.status === 'PRN_GIVEN'));
        setRecords(filtered.slice(0, 20));
        setTotal(filtered.length);
      } catch (e) { toast.error('Failed to load medication data'); }
      finally { setLoading(false); }
    };
    load();
  }, [statusFilter]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="medication-dashboard-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Medication Dashboard</h1>
        <p className="text-muted-foreground mt-1">eMAR overview and alerts</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="medications-summary">
        <Card className="cursor-pointer hover:border-primary/30" onClick={() => setStatusFilter('ADMINISTERED')} data-testid="medications-summary-administered">
          <CardContent className="p-4"><Check className="h-5 w-5 text-emerald-500 mb-2" /><p className="text-2xl font-bold tabular-nums">{stats.administered}</p><p className="text-xs text-muted-foreground">Administered</p></CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-destructive/30" onClick={() => setStatusFilter('MISSED')} data-testid="medications-summary-missed">
          <CardContent className="p-4"><AlertTriangle className="h-5 w-5 text-red-500 mb-2" /><p className="text-2xl font-bold tabular-nums">{stats.missed}</p><p className="text-xs text-muted-foreground">Missed</p></CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-amber-500/30" onClick={() => setStatusFilter('REFUSED')} data-testid="medications-summary-refused">
          <CardContent className="p-4"><X className="h-5 w-5 text-amber-500 mb-2" /><p className="text-2xl font-bold tabular-nums">{stats.refused}</p><p className="text-xs text-muted-foreground">Refused</p></CardContent>
        </Card>
        <Card className="cursor-pointer" onClick={() => setStatusFilter('WITHHELD')} data-testid="medications-summary-withheld">
          <CardContent className="p-4"><Pill className="h-5 w-5 text-blue-500 mb-2" /><p className="text-2xl font-bold tabular-nums">{stats.withheld}</p><p className="text-xs text-muted-foreground">Withheld</p></CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="medications-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="ADMINISTERED">Administered</SelectItem>
            <SelectItem value="MISSED">Missed</SelectItem>
            <SelectItem value="REFUSED">Refused</SelectItem>
            <SelectItem value="WITHHELD">Withheld</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} record(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={5} columns={5} testid="medications-loading" />
          ) : records.length === 0 ? (
            <EmptyState
              icon={Pill}
              title={statusFilter !== 'all' ? `No ${statusFilter.toLowerCase()} records` : 'No medication records yet'}
              description={statusFilter !== 'all' ? 'Try switching to All Statuses to see other records.' : 'Medication administration history will appear here as it is recorded.'}
              testid="medications-empty"
            />
          ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Medication</TableHead><TableHead>Dosage</TableHead><TableHead>Time</TableHead><TableHead>Status</TableHead><TableHead>Staff</TableHead></TableRow></TableHeader>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.id} data-testid={`medication-row-${r.id}`}>
                  <TableCell className="font-medium">{r.medication}</TableCell>
                  <TableCell className="text-sm">{r.dosage}</TableCell>
                  <TableCell className="font-mono text-xs">{r.scheduled_time}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-sm">{r.staff || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
